import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useCallback
} from 'react'
import { MixedCardItem } from '../../Teams/DataboxItems'
import { useSelector } from 'react-redux'
import { parseToMixedItems, extractOriginalDataSourceIds } from './dataSourceUtils'
import { useDataboxService } from '../../Teams/services/databoxService'
import {
  extractCognitiveSearchIdsFromOData,
  extractDataboxIdsFromOData,
  sumFormattedSizes
} from '../utils/helpers'
import { MCP_SERVER_NAMES } from '../utils/constants'
import { type OriginalDataSourcesByMcp, parseScopeConfiguration } from '../utils/mcpServerUtils'

interface AgentFormContextType {
  assistantName: string
  setAssistantName: (value: string) => void
  selectedAvatar: File | string | null
  setSelectedAvatar: (value: File | string | null) => void
  assistantDescription: string
  setAssistantDescription: (value: string) => void
  selectedChatModel: any
  setSelectedChatModel: (value: any) => void
  instructions: string
  setInstructions: (value: string) => void
  selectedAccessOption: string
  setSelectedAccessOption: (value: string) => void
  selectedSkills: any[]
  setSelectedSkills: (value: any[]) => void
  avatarFile: File | null
  setAvatarFile: (value: File | null) => void
  setSelectedDataBoxes: (value: any) => void
  setSelectedDataProducts: (value: any) => void
  isAddDataboxModalOpen: boolean
  setIsAddDataboxModalOpen: (value: any) => void
  editingDataboxId: string | null
  setEditingDataboxId: (value: string | null) => void
  isAddDataProductModalOpen: boolean
  setIsAddDataProductModalOpen: (value: any) => void
  isAddSourceModalOpen: boolean
  setIsAddSourceModalOpen: (value: any) => void
  mixedAssetData: MixedCardItem[]
  setMixedAssetData: (value: MixedCardItem[] | ((prev: MixedCardItem[]) => MixedCardItem[])) => void
  removeFromMixedAssetData: (ids: string[]) => void
  originalDataSourcesByMcp: OriginalDataSourcesByMcp | null
  isDataSourcesInitialized: boolean
  isMemoryEnabled: boolean
  setIsMemoryEnabled: (value: boolean) => void
  isWebMCPEnabled: boolean
  setIsWebMCPEnabled: (value: boolean) => void
}

const AgentFormContext = createContext<AgentFormContextType | undefined>(undefined)

export const AgentFormProvider = ({ children }: { children: ReactNode }) => {
  const [assistantName, setAssistantName] = useState('')
  const [selectedAvatar, setSelectedAvatar] = useState<File | string | null>(null)
  const [assistantDescription, setAssistantDescription] = useState<any>('')
  const [selectedChatModel, setSelectedChatModel] = useState<any>(null)
  const [instructions, setInstructions] = useState('')
  const [selectedAccessOption, setSelectedAccessOption] = useState('Personal')
  const [selectedSkills, setSelectedSkills] = useState<any[]>([])
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [isMemoryEnabled, setIsMemoryEnabled] = useState(false)
  const [isWebMCPEnabled, setIsWebMCPEnabled] = useState(false)

  const [filteredData, setFilteredData] = useState<MixedCardItem[]>([])
  const [isDataSourcesInitialized, setIsDataSourcesInitialized] = useState(false)
  const [originalDataSourcesByMcp, setOriginalDataSourcesByMcp] =
    useState<OriginalDataSourcesByMcp | null>(null)
  // Bumps every time the agent (or its agentMcpServers) changes; a load whose id no longer
  // matches the latest value is stale, so its result is ignored instead of overwriting the UI.
  const loadDataSourcesRequestIdRef = useRef(0)

  // to open modal using drop-down
  const [isAddDataboxModalOpen, setIsAddDataboxModalOpen] = useState(false)
  const [editingDataboxId, setEditingDataboxId] = useState<string | null>(null)
  const [isAddDataProductModalOpen, setIsAddDataProductModalOpen] = useState(false)
  // Opened by the generic "Add source" button - lands on the personal databox tab
  const [isAddSourceModalOpen, setIsAddSourceModalOpen] = useState(false)

  const { agent } = useSelector((state: any) => state.agentReducer)
  const { mcpServers } = useSelector((state: any) => state.chatbotReducer)
  const { fetchDataSourceDetails, getMyDataboxes, getDataboxDocumentsPaginated } =
    useDataboxService()

  // Reset state when agent changes (switching between agents or creating new)
  useEffect(() => {
    // Clear data sources state
    setIsDataSourcesInitialized(false)
    setFilteredData([])
    setOriginalDataSourcesByMcp(null)
    // Bump so a load still running for the previous agent gets ignored when it resolves.
    loadDataSourcesRequestIdRef.current += 1

    if (agent?.id) {
      // Editing existing agent - populate form with agent's values
      setAssistantName(agent.name || '')
      setAssistantDescription(agent.description || '')
      setInstructions(agent.instructions || '')
      setSelectedAccessOption(agent.agentType || 'Personal')
      setIsMemoryEnabled(agent.enableMemory || false)
      setSelectedAvatar(agent.avatarUrl || null)
    } else {
      // Creating new agent - clear all form fields
      setAssistantName('')
      setAssistantDescription('')
      setInstructions('')
      setSelectedAccessOption('Personal')
      setIsMemoryEnabled(false)
      setSelectedAvatar(null)
      setSelectedChatModel(null)
      setSelectedSkills([])
      setAvatarFile(null)
      setIsWebMCPEnabled(true)
    }
  }, [agent?.id])

  // Initialize data sources from agent details when in edit mode
  useEffect(() => {
    // If no agent exists and no MCP servers are configured, mark data sources as initialized with empty data
    if (!agent?.id && mcpServers?.length === 0) {
      setIsDataSourcesInitialized(true)
      return
    }

    // Reset initialized flag to force reload when agentMcpServers changes
    setIsDataSourcesInitialized(false)

    // Claim this load's id - if agent/agentMcpServers change again before it finishes, it becomes stale.
    const requestId = ++loadDataSourcesRequestIdRef.current

    const loadDataSources = async () => {
      const hasTeamScopedMcp = agent.agentMcpServers && agent.agentMcpServers.length > 0
      const hasPersonalDataBoxes = agent.personalDataBoxes && agent.personalDataBoxes.length > 0

      if (hasTeamScopedMcp || hasPersonalDataBoxes) {
        try {
          // Extract original data source IDs (organized by MCP + flat arrays for fetching) -
          // team-scoped MCP servers only. Personal databox is read from the separate top-level
          // agent.personalDataBoxes field below.
          const {
            dataSourcesByMcp,
            allDataBoxIds,
            allDataProductIds,
            dataProductsByDatabox,
            otherMCPs
          } = extractOriginalDataSourceIds(agent.agentMcpServers || [])

          //set web search MCP
          const WebSearchMCP =
            mcpServers?.find((server: any) => server.name === MCP_SERVER_NAMES.WEB_SEARCH) || null
          setIsWebMCPEnabled(
            WebSearchMCP ? otherMCPs.some((mcpId: any) => mcpId === WebSearchMCP.id) : false
          )

          // Store organized structure for change detection
          setOriginalDataSourcesByMcp(dataSourcesByMcp)

          // Extract all cognitive search IDs from OData filters to fetch document details
          const allCognitiveSearchIds: string[] = []
          ;(agent.agentMcpServers || []).forEach((server: any) => {
            const parsedConfig = parseScopeConfiguration(server.scopeConfiguration)
            if (parsedConfig) {
              const odataFilter = parsedConfig.filters || ''
              if (odataFilter) {
                const cogIds = extractCognitiveSearchIdsFromOData(odataFilter)
                cogIds.forEach((id: string) => {
                  if (!allCognitiveSearchIds.includes(id)) {
                    allCognitiveSearchIds.push(id)
                  }
                })
              }
            }
          })

          // Fetch full details for databoxes and data products using flat arrays
          const { databoxes, dataProducts } = await fetchDataSourceDetails(
            allDataBoxIds,
            allDataProductIds,
            allCognitiveSearchIds,
            dataProductsByDatabox
          )

          // A newer load started while this fetch was in flight (agent switched, path
          // changed, or agentMcpServers changed again) - discard this stale result.
          if (loadDataSourcesRequestIdRef.current !== requestId) {
            return
          }

          // Parse and set the mixed items
          const mixedItems = parseToMixedItems(databoxes, dataProducts)

          // Attach teamIdentifier and filter selectedItems based on OData filter
          ;(agent.agentMcpServers || []).forEach((server: any) => {
            const parsedConfig = parseScopeConfiguration(server.scopeConfiguration)
            if (parsedConfig) {
              const odataFilter = parsedConfig.filters || ''
              const serverDataBoxIds = extractDataboxIdsFromOData(odataFilter)
              const cognitiveSearchIds = odataFilter
                ? extractCognitiveSearchIdsFromOData(odataFilter)
                : []

              mixedItems.forEach((item: any) => {
                if (item.type === 'databox') {
                  const dbId = String(item.smartDocsDataboxId || item.id)
                  if (serverDataBoxIds.includes(dbId)) {
                    if (parsedConfig.teamIdentifier) {
                      item.teamIdentifier = parsedConfig.teamIdentifier
                    }

                    if (cognitiveSearchIds.length > 0 && item.selectedItems?.length > 0) {
                      const cognitiveSearchIdSet = new Set(cognitiveSearchIds)

                      item.selectedItems = item.selectedItems.filter((selectedItem: any) => {
                        if (selectedItem.type === 'file') {
                          return cognitiveSearchIdSet.has(selectedItem.cognitiveSearchId)
                        }
                        return selectedItem.type === 'dataproduct'
                      })

                      item.selectedFilesCount =
                        item.selectedItems?.filter((si: any) => si.type === 'file').length || 0
                      item.selectedDataProductsCount =
                        item.selectedItems?.filter((si: any) => si.type === 'dataproduct').length ||
                        0
                    }
                  }
                }
              })
            }
          })

          // Resolve the Personal Databox tab's own selection (resourceIds) into a full databox
          // item with named/sized selectedItems - read from agent.personalDataBoxes field.
          const personalDataBoxEntry = agent.personalDataBoxes?.[0]
          const personalDataboxResourceIds: string[] = (
            personalDataBoxEntry?.resourceIds ?? []
          ).map(String)
          const personalDataBoxId = personalDataBoxEntry?.personalDataBoxId

          if (personalDataboxResourceIds.length > 0) {
            try {
              const myDataboxesResponse = await getMyDataboxes()
              const myDataboxes = myDataboxesResponse?.items ?? myDataboxesResponse ?? []
              const personalDatabox = (Array.isArray(myDataboxes) ? myDataboxes : []).find(
                (db: any) =>
                  personalDataBoxId
                    ? String(db?.id) === String(personalDataBoxId) ||
                      String(db?.smartDocsDataboxId) === String(personalDataBoxId)
                    : String(db?.scope ?? '').toLowerCase() === 'personal'
              )

              if (personalDatabox) {
                const docsResponse = await getDataboxDocumentsPaginated(
                  personalDatabox.id,
                  1,
                  100,
                  undefined,
                  'uploadTime',
                  'desc'
                )
                const docs = Array.isArray(docsResponse) ? docsResponse : []
                const resourceIdSet = new Set(personalDataboxResourceIds.map(String))
                const matchedDocs = docs.filter((doc: any) =>
                  resourceIdSet.has(String(doc.id ?? doc.smartDocsDocumentId ?? doc.externalId))
                )

                if (loadDataSourcesRequestIdRef.current === requestId && matchedDocs.length > 0) {
                  const selectedItems = matchedDocs.map((doc: any) => ({
                    id: String(doc.id ?? doc.smartDocsDocumentId ?? doc.externalId),
                    name: doc.name ?? 'Untitled file',
                    type: 'file',
                    sizeFormatted: doc.sizeFormatted
                  }))
                  mixedItems.push({
                    id: String(personalDatabox.id),
                    name: personalDatabox.name ?? 'My Personal Databox',
                    type: 'databox',
                    scope: 'Personal',
                    source: 'smartDocs',
                    owner: '',
                    dateAdded: new Date().toISOString(),
                    filesCount:
                      personalDatabox.filesCount ?? personalDatabox.databoxDocumentsCount ?? 0,
                    dataProductsCount: 0,
                    selectedFilesCount: selectedItems.length,
                    selectedDataProductsCount: 0,
                    selectedItems,
                    totalSizeFormatted: sumFormattedSizes(
                      selectedItems.map((item: any) => item.sizeFormatted)
                    )
                  } as any)
                }
              }
            } catch (err) {
              console.error('Error loading personal databox selection:', err)
            }
          }

          setFilteredData(mixedItems)
          setIsDataSourcesInitialized(true)
        } catch (error) {
          console.error('Error loading data sources:', error)
        }
      } else {
        // Agent exists but has no MCP servers or empty array - mark as initialized
        setIsDataSourcesInitialized(true)
        setFilteredData([])
        setIsWebMCPEnabled(false)
      }
    }

    loadDataSources()
    // agentMcpServers/personalDataBoxes are compared by content (JSON.stringify) since a new
    // array reference with the same contents shouldn't trigger a reload
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agent?.id,
    JSON.stringify(agent?.agentMcpServers),
    JSON.stringify(agent?.personalDataBoxes),
    mcpServers?.length
  ])

  const setSelectedDataBoxes = useCallback((databox: any) => {
    setFilteredData((data: MixedCardItem[]) => {
      const allNewItems: MixedCardItem[] = []

      databox.forEach((db: any) => {
        // Normalize selected items tags to strings
        const normalizedSelectedItems = db.selectedItems?.map((item: any) => {
          const normalized = {
            ...item,
            tags:
              item.tags?.map((tag: any) => {
                if (typeof tag === 'string') return tag
                if (tag && typeof tag === 'object' && tag.name) return tag.name
                return String(tag)
              }) || []
          }
          return normalized
        })

        // Calculate actual selected counts
        const selectedFilesCount =
          normalizedSelectedItems?.filter((item: any) => item.type === 'file').length || 0
        const selectedDataProductsCount =
          normalizedSelectedItems?.filter((item: any) => item.type === 'dataproduct').length || 0

        // Preserve all original databox properties and add UI-specific fields
        const databoxItem: MixedCardItem = {
          ...db, // Keep all original properties (including smartDocsDataboxId, etc.)
          type: 'databox',
          source: db.source || 'smartDocs',
          owner: db.createdBy?.displayName ?? db.owner ?? '',
          // Falls back to now when neither dateAdded nor a valid createdAt is present
          dateAdded:
            db.dateAdded ||
            (db.createdAt ? new Date(db.createdAt).toISOString() : new Date().toISOString()),
          // Keep original total counts, don't override
          filesCount: db.filesCount || db.databoxDocumentsCount || 0,
          dataProductsCount: db.dataProductsCount || db.databoxDataProductsCount || 0,
          // Store selected counts separately
          selectedFilesCount,
          selectedDataProductsCount,
          selectedItems: normalizedSelectedItems || db.selectedItems,
          // The Personal Databox tab has no server-provided totalSizeFormatted
          totalSizeFormatted:
            db.totalSizeFormatted ??
            (normalizedSelectedItems?.some((item: any) => item.sizeFormatted)
              ? sumFormattedSizes(normalizedSelectedItems.map((item: any) => item.sizeFormatted))
              : db.totalSizeFormatted)
        }
        allNewItems.push(databoxItem)
      })

      // Build a set of IDs being updated (check both id and smartDocsDataboxId)
      const updatedDataboxIds = new Set<string>()
      allNewItems.forEach((item) => {
        if (item.id) updatedDataboxIds.add(String(item.id))
        if ((item as any).smartDocsDataboxId) {
          updatedDataboxIds.add(String((item as any).smartDocsDataboxId))
        }
      })

      // Keep existing databoxes that are NOT being updated
      // A databox is considered the same if ANY of its IDs match ANY of the incoming IDs
      const existingDataboxes = data.filter((item) => {
        if (item.type !== 'databox') return false

        // Collect all IDs from this existing databox
        const existingIds = [
          item.id ? String(item.id) : null,
          (item as any).smartDocsDataboxId ? String((item as any).smartDocsDataboxId) : null
        ].filter(Boolean) as string[]

        // Check if ANY of the existing IDs match ANY of the updated IDs
        const hasMatch = existingIds.some((existingId) => updatedDataboxIds.has(existingId))

        // Keep if NO match found (this databox is not being updated)
        return !hasMatch
      })

      // Keep standalone files/data products that were added directly (not from databoxes)
      const standaloneItems = data.filter((item) => {
        return item.type !== 'databox' && !item.fromDataboxId
      })

      // Return: standalone items + existing databoxes (not updated) + new/updated databoxes
      const finalData = [...standaloneItems, ...existingDataboxes, ...allNewItems]

      return finalData
    })
  }, [])

  const setSelectedDataProducts = useCallback((dataProduct: any) => {
    setFilteredData((data: MixedCardItem[]) => {
      const dataProductItem = dataProduct.map(
        ({ name, id, metadataSource, tier, securityClassification, owners, description }: any) => {
          return {
            type: 'dataproduct',
            name,
            id,
            owner: owners?.join(', ') ?? '',
            dateAdded: new Date().toISOString(),
            tier,
            securityClassification,
            source: metadataSource,
            description
          }
        }
      )
      // Filter out duplicates
      const existingIds = new Set(data.map((item) => item.id))
      const newItems = dataProductItem.filter((item: MixedCardItem) => !existingIds.has(item.id))
      return [...data, ...newItems]
    })
  }, [])

  const removeFromMixedAssetData = useCallback((ids: string[]) => {
    const idSet = new Set(ids.map(String))
    setFilteredData((data: MixedCardItem[]) =>
      data.filter((item) => {
        const itemIds = [String(item.id), String((item as any).smartDocsDataboxId || '')]
        return !itemIds.some((id) => idSet.has(id))
      })
    )
  }, [])

  return (
    <AgentFormContext
      value={{
        assistantName,
        setAssistantName,
        selectedAvatar,
        setSelectedAvatar,
        assistantDescription,
        setAssistantDescription,
        selectedChatModel,
        setSelectedChatModel,
        instructions,
        setInstructions,
        selectedAccessOption,
        setSelectedAccessOption,
        selectedSkills,
        setSelectedSkills,
        avatarFile,
        setAvatarFile,
        setSelectedDataBoxes,
        setSelectedDataProducts,
        isAddDataboxModalOpen,
        setIsAddDataboxModalOpen,
        editingDataboxId,
        setEditingDataboxId,
        isAddDataProductModalOpen,
        setIsAddDataProductModalOpen,
        isAddSourceModalOpen,
        setIsAddSourceModalOpen,
        mixedAssetData: filteredData,
        isDataSourcesInitialized,
        setMixedAssetData: setFilteredData,
        removeFromMixedAssetData,
        originalDataSourcesByMcp,
        isMemoryEnabled,
        setIsMemoryEnabled,
        isWebMCPEnabled,
        setIsWebMCPEnabled
      }}>
      {children}
    </AgentFormContext>
  )
}

export const useAgentContext = () => {
  const context = useContext(AgentFormContext)
  if (!context) {
    throw new Error('useAgentContext must be used within AgentFormProvider')
  }
  return context
}
